/**
 * 管理后台「运行与安全 B」文案（韩、中、英）：视频记录、音频记录、反滥用、会话票据、配置变更、降级与告警。
 *
 * 背景：页面按 Alice 的版式复刻，文案保持 Alice 的措辞，品牌与数据来源按本仓适配（没有网盘、没有客户端票据）。
 * 设计意图：按页面分组；后端新增的错误码在这里映射（common.ts 的错误表归共用文件，不在本页改），没映射到的回落到共用表。
 * 关键约束：韩文用 해요체；中文不用破折号和英文引号。
 */
import { errorText as baseErrorText } from './common.ts'
import { translate, type Entry, type Locale } from './core.tsx'

const e = (zh: string, ko: string, en: string): Entry => ({ zh, ko, en })

const CODES: Record<string, Entry> = {
  task_not_found: e('找不到這個影片任務', '영상 작업을 찾을 수 없어요', 'Video task not found'),
  not_reviewable: e('只有已成功的影片可以審核', '성공한 영상만 심사할 수 있어요', 'Only succeeded videos can be reviewed'),
  invalid_status: e('任務狀態篩選值不合法', '작업 상태 필터 값이 올바르지 않아요', 'Invalid status filter'),
  invalid_review_status: e('審核狀態篩選值不合法', '심사 상태 필터 값이 올바르지 않아요', 'Invalid review status filter'),
  invalid_kind: e('音頻類型篩選值不合法', '오디오 유형 필터 값이 올바르지 않아요', 'Invalid audio kind filter'),
  invalid_action: e('動作不合法', '동작이 올바르지 않아요', 'Invalid action'),
  invalid_time: e('時間格式不合法', '시간 형식이 올바르지 않아요', 'Invalid time format'),
}

/** 本组页面的错误提示：先查本组新增的错误码，其余沿用共用错误表。 */
export function errorText2(err: unknown, locale: Locale): string {
  const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : ''
  const entry = CODES[code]
  return entry ? translate(entry, locale) : baseErrorText(err, locale)
}

export const AG_runtime2 = {
  common: {
    filter: e('篩選', '필터', 'Filter'),
    clear: e('清空', '초기화', 'Clear'),
    query: e('查詢', '조회', 'Search'),
    loading: e('載入中...', '불러오는 중이에요...', 'Loading...'),
    to: e('至', '~', 'to'),
    prev: e('上一頁', '이전', 'Previous'),
    next: e('下一頁', '다음', 'Next'),
    pageOf: e('第 {p} / {n} 頁', '{p} / {n} 페이지', 'Page {p} of {n}'),
    cancel: e('取消', '취소', 'Cancel'),
    confirm: e('確定', '확인', 'Confirm'),
    refresh: e('刷新', '새로고침', 'Refresh'),
    dash: e('—', '—', '—'),
    seconds: e('{n} 秒', '{n}초', '{n}s'),
    characters: e('{n} 字', '{n}자', '{n} chars'),
    requestId: e('請求 ID', '요청 ID', 'Request ID'),
    loadFailed: e('載入失敗：{msg}', '불러오지 못했어요: {msg}', 'Load failed: {msg}'),
    unsettled: e('未結算', '정산 전', 'Unsettled'),
    costUnknown: e('成本未知', '원가 미확인', 'Cost unknown'),
    cost: e('成本 {v}', '원가 {v}', 'Cost {v}'),
    accountFallback: e('帳號 {id}', '계정 {id}', 'Account {id}'),
    manual: e(' · 人工', ' · 수동', ' · manual'),
  },

  videos: {
    eyebrow: e('VIDEO OPERATIONS', 'VIDEO OPERATIONS', 'VIDEO OPERATIONS'),
    title: e('影片記錄', '영상 기록', 'Video records'),
    desc: e('查看異步生成任務、結算結果與人工審核', '비동기 생성 작업, 정산 결과와 수동 심사를 확인해요', 'Async generation tasks, settlement results and manual review'),
    chip: e('任務與結算全鏈路', '작업과 정산 전 과정', 'Task and settlement trail'),
    statTotal: e('影片任務', '영상 작업', 'Video tasks'),
    statTotalSub: e('成功 {ok} · 進行中 {active} · 失敗/取消 {failed}', '성공 {ok} · 진행 중 {active} · 실패/취소 {failed}', 'OK {ok} · active {active} · failed/cancelled {failed}'),
    statSeconds: e('請求時長', '요청 길이', 'Requested duration'),
    statSecondsSub: e('按任務請求參數匯總', '작업 요청 값 기준 합계', 'Summed from task parameters'),
    statReview: e('違規 / 疑似', '위반 / 의심', 'Violation / suspect'),
    statReviewSub: e('待審核 {n} 條', '심사 대기 {n}건', '{n} pending review'),
    statCharge: e('使用者實扣 / 毛利', '사용자 차감 / 마진', 'Charged / margin'),
    statChargeSub: e('已知樣本毛利 {m} · 成本覆蓋 {n} 單', '확인된 표본 마진 {m} · 원가 확인 {n}건', 'Margin on known samples {m} · cost known for {n}'),
    userPh: e('帳號 / Key ID 或終端使用者', '계정 / 키 ID 또는 최종 사용자', 'Account / key ID or end user'),
    modelPh: e('模型', '모델', 'Model'),
    promptPh: e('提示詞關鍵字', '프롬프트 키워드', 'Prompt keyword'),
    statusAll: e('全部任務狀態', '전체 작업 상태', 'All task statuses'),
    status: {
      submitting: e('提交中', '제출 중', 'Submitting'),
      queued: e('排隊中', '대기 중', 'Queued'),
      running: e('生成中', '생성 중', 'Running'),
      succeeded: e('已成功', '성공', 'Succeeded'),
      failed: e('生成失敗', '생성 실패', 'Failed'),
      cancelled: e('已取消', '취소됨', 'Cancelled'),
    } as Record<string, Entry>,
    reviewAll: e('全部審核狀態', '전체 심사 상태', 'All review statuses'),
    reviewFlagged: e('疑似 + 違規', '의심 + 위반', 'Suspect + violation'),
    reviewOptions: {
      violation: e('違規（已停播）', '위반 (재생 중지)', 'Violation (blocked)'),
      suspect: e('疑似違規', '위반 의심', 'Suspect'),
      passed: e('已通過', '통과', 'Passed'),
      pending: e('待審核', '심사 대기', 'Pending'),
      failed: e('審核失敗', '심사 실패', 'Review failed'),
      skipped: e('未審核', '심사 안 함', 'Skipped'),
    } as Record<string, Entry>,
    review: {
      pending: e('待審核', '심사 대기', 'Pending review'),
      passed: e('審核通過', '심사 통과', 'Passed'),
      suspect: e('疑似違規', '위반 의심', 'Suspect'),
      violation: e('違規已停播', '위반으로 재생 중지', 'Blocked as violation'),
      failed: e('審核失敗已放行', '심사 실패, 허용됨', 'Review failed, released'),
      skipped: e('未審核', '심사 안 함', 'Skipped'),
      noFile: e('無成片不審', '결과물이 없어 심사 안 함', 'No video to review'),
    } as Record<string, Entry>,
    totalBadge: e('共 {total} 條 · 違規 {v} · 疑似 {s} · 待審 {p}', '총 {total}건 · 위반 {v} · 의심 {s} · 대기 {p}', '{total} total · {v} violation · {s} suspect · {p} pending'),
    note: e(
      '成片地址由上遊直接提供，影片任務在 Redis 裡保留 7 天，超過後只剩用量記錄，播放連結與提示詞不再顯示。提示詞檢索只對請求正文仍在保存期內的記錄生效，進行中的任務按記憶體裡的提示詞匹配。審核由運營人工完成：判為違規後，使用者查詢任務時拿不到播放地址，改判通過或重置後恢復；本倉沒有自動審核模型，也沒有網盤與歸檔副本。',
      '결과 영상 주소는 업스트림이 직접 주는 값이에요. 영상 작업은 Redis에 7일 동안 남고, 그 뒤에는 사용량 기록만 남아 재생 링크와 프롬프트가 보이지 않아요. 프롬프트 검색은 요청 본문이 보관 기간 안에 있는 기록에만 적용돼요. 심사는 운영자가 수동으로 해요. 위반으로 판정하면 사용자가 작업을 조회해도 재생 주소를 받지 못하고, 통과로 바꾸거나 초기화하면 다시 열려요. 자동 심사 모델과 드라이브, 보관 사본은 없어요.',
      'The video URL comes straight from the upstream. Tasks stay in Redis for 7 days; after that only the usage record remains and the play link and prompt are no longer shown. Prompt search only covers records whose request bodies are still retained. Review is manual: once blocked, the user cannot get the play URL when polling the task; passing or resetting restores it. There is no automatic review model, drive or archive copy.',
    ),
    sectionTitle: e('生成記錄', '생성 기록', 'Generations'),
    sectionCopy: e('進行中的任務每 10 秒自動刷新', '진행 중인 작업은 10초마다 자동으로 새로고침돼요', 'Active tasks refresh every 10 seconds'),
    empty: e('暫無影片記錄', '영상 기록이 없어요', 'No video records'),
    noPrompt: e('(提示詞已過期或未保存)', '(프롬프트가 만료되었거나 저장되지 않았어요)', '(prompt expired or not stored)'),
    openNew: e('新窗口播放', '새 창에서 재생', 'Open in new window'),
    noFile: e('沒有成片', '결과물 없음', 'No video yet'),
    actionPass: e('改判通過', '통과로 변경', 'Mark as passed'),
    actionBlock: e('判為違規', '위반으로 판정', 'Block as violation'),
    actionRetry: e('重置為待審核', '심사 대기로 초기화', 'Reset to pending'),
    confirmPass: e('改判通過後，使用者可以重新播放這支影片。確定嗎？', '통과로 바꾸면 사용자가 이 영상을 다시 재생할 수 있어요. 진행할까요?', 'The user will be able to play this video again. Continue?'),
    confirmBlock: e('判為違規後，使用者側會停止播放這支影片。確定嗎？', '위반으로 판정하면 사용자 쪽에서 이 영상 재생이 중지돼요. 진행할까요?', 'The user will no longer be able to play this video. Continue?'),
    confirmRetry: e('重置為待審核？使用者側恢復可播放，等待下一次人工判定。', '심사 대기로 초기화할까요? 사용자 쪽 재생이 다시 열리고, 다음 수동 판정을 기다려요.', 'Reset to pending? Playback is restored until the next manual decision.'),
    updated: e('審核狀態已更新', '심사 상태를 바꿨어요', 'Review status updated'),
    notEnforced: e('審核狀態已更新（任務已過期，使用者側無需改動）', '심사 상태를 바꿨어요 (작업이 만료되어 사용자 쪽은 그대로예요)', 'Review updated (task expired, nothing to change for the user)'),
    start: e('開始日期', '시작일', 'Start date'),
    end: e('結束日期', '종료일', 'End date'),
  },

  audios: {
    eyebrow: e('AUDIO OPERATIONS', 'AUDIO OPERATIONS', 'AUDIO OPERATIONS'),
    title: e('音頻記錄', '오디오 기록', 'Audio records'),
    desc: e('語音合成與語音轉寫的請求、計費與結算結果', '음성 합성과 음성 전사의 요청, 과금, 정산 결과', 'Speech synthesis and transcription requests, billing and settlement'),
    chip: e('請求與結算全鏈路', '요청과 정산 전 과정', 'Request and settlement trail'),
    statTotal: e('音頻請求', '오디오 요청', 'Audio requests'),
    statTotalSub: e('成功 {ok} · 失敗 {failed}', '성공 {ok} · 실패 {failed}', 'OK {ok} · failed {failed}'),
    statKinds: e('請求構成', '요청 구성', 'Composition'),
    statKindsSub: e('語音合成 / 語音轉寫 · 合成 {c} 字 · 轉寫 {s} 秒', '음성 합성 / 전사 · 합성 {c}자 · 전사 {s}초', 'Speech / transcription · {c} chars · {s}s transcribed'),
    statRate: e('成功率', '성공률', 'Success rate'),
    statRateSub: e('同步請求，不入網盤', '동기 요청, 드라이브에 저장 안 함', 'Synchronous, no file storage'),
    statCharge: e('使用者實扣 / 毛利', '사용자 차감 / 마진', 'Charged / margin'),
    statChargeSub: e('已知樣本毛利 {m} · 成本覆蓋 {n} 單', '확인된 표본 마진 {m} · 원가 확인 {n}건', 'Margin on known samples {m} · cost known for {n}'),
    kindAll: e('全部類型', '전체 유형', 'All kinds'),
    kind: {
      speech: e('語音合成', '음성 합성', 'Speech'),
      transcription: e('語音轉寫', '음성 전사', 'Transcription'),
    } as Record<string, Entry>,
    userPh: e('帳號 / Key ID 或終端使用者', '계정 / 키 ID 또는 최종 사용자', 'Account / key ID or end user'),
    modelPh: e('模型', '모델', 'Model'),
    keywordPh: e('合成文本關鍵字', '합성 텍스트 키워드', 'Synthesized text keyword'),
    statusAll: e('全部任務狀態', '전체 요청 상태', 'All statuses'),
    status: {
      succeeded: e('成功', '성공', 'Succeeded'),
      failed: e('失敗', '실패', 'Failed'),
    } as Record<string, Entry>,
    totalBadge: e('共 {total} 條', '총 {total}건', '{total} total'),
    note: e(
      '語音合成與語音轉寫都是同步請求，不建任務、不入網盤、不保存音頻文件，所以卡片上沒有播放器，也沒有審核與歸檔；記錄來自每次請求結算後的用量記錄。合成文本只在請求正文仍在保存期內時才有，關鍵字檢索同樣只對這部分記錄生效。轉寫時長按上遊返回的實際音頻時長計費。',
      '음성 합성과 음성 전사는 모두 동기 요청이라 작업을 만들지 않고 드라이브에도, 오디오 파일에도 저장하지 않아요. 그래서 카드에 플레이어와 심사, 보관이 없고, 기록은 요청이 끝난 뒤 정산된 사용량 기록에서 가져와요. 합성 텍스트는 요청 본문이 보관 기간 안에 있을 때만 있고, 키워드 검색도 그 기록에만 적용돼요. 전사 요금은 업스트림이 돌려준 실제 오디오 길이로 계산해요.',
      'Speech synthesis and transcription are synchronous: no task, no drive file and no stored audio, so cards have no player, review or archive. Records come from the usage rows written at settlement. Synthesized text exists only while the request body is retained, and keyword search only covers those. Transcription is billed by the actual audio duration the upstream reports.',
    ),
    sectionTitle: e('請求記錄', '요청 기록', 'Requests'),
    sectionCopy: e('每次請求結算後落一條用量記錄', '요청이 정산될 때마다 사용량 기록이 하나 남아요', 'One usage row per settled request'),
    empty: e('暫無音頻記錄', '오디오 기록이 없어요', 'No audio records'),
    placeholderSpeech: e('同步合成不保存音頻文件', '동기 합성은 오디오 파일을 저장하지 않아요', 'Synchronous synthesis stores no audio file'),
    placeholderAsr: e('轉寫請求的音頻不保存，只保留時長', '전사 요청의 오디오는 저장하지 않고 길이만 남겨요', 'Transcription audio is not stored, only its duration'),
    titleSpeech: e('語音合成', '음성 합성', 'Speech synthesis'),
    titleAsr: e('語音轉寫', '음성 전사', 'Transcription'),
    noText: e('(未保存文本)', '(텍스트가 저장되지 않았어요)', '(text not stored)'),
    detailAsr: e('音頻時長 {s} 秒', '오디오 길이 {s}초', 'Audio {s}s'),
    attempts: e('{n} 次嘗試', '{n}회 시도', '{n} attempts'),
    start: e('開始日期', '시작일', 'Start date'),
    end: e('結束日期', '종료일', 'End date'),
  },

  abuse: {
    eyebrow: e('TRUST & SAFETY', 'TRUST & SAFETY', 'TRUST & SAFETY'),
    title: e('反濫用', '어뷰징 방지', 'Abuse control'),
    desc: e('查看帳號封禁與解封記錄，跟蹤被鎖定的高風險帳號', '계정 정지와 해제 기록을 보고 잠긴 고위험 계정을 추적해요', 'Account bans and lifts, and the high-risk accounts that are locked'),
    chip: e('安全策略持續生效', '보안 정책 상시 적용', 'Safety policy active'),
    statTotal: e('累計封禁次數', '누적 정지 횟수', 'Total bans'),
    statActive: e('當前生效', '현재 적용 중', 'Active now'),
    statUsers: e('涉及帳號', '관련 계정', 'Accounts involved'),
    policyTitle: e('封禁與解封規則', '정지와 해제 규칙', 'Ban and lift rules'),
    policyCopy: e('展開查看封禁的效果、解封方式和本倉沒有的檢測項', '정지 효과, 해제 방법, 이 시스템에 없는 탐지 항목을 펼쳐서 확인해요', 'Expand to see what a ban does, how to lift it and what is not detected here'),
    policy: {
      banTitle: e('封禁（帳號級，管理員手動執行）', '정지 (계정 단위, 관리자가 수동 실행)', 'Ban (account level, done manually by an admin)'),
      ban: [
        e('管理員在「使用者與計費」裡封禁帳號，必須填寫原因，操作寫進審計。', '관리자가 「사용자와 과금」에서 계정을 정지해요. 사유를 꼭 적어야 하고 감사 기록에 남아요.', 'An admin bans an account under Users & billing; a reason is required and the action is audited.'),
        e('封禁會鎖定帳號（控制台與管理後台都登入不了），作廢它的全部會話，資料面立即拒絕它名下 Key 的請求。', '정지하면 계정이 잠기고(콘솔과 관리자 모두 로그인 불가) 모든 세션이 무효가 되며 데이터 플레인이 그 계정의 키 요청을 바로 거부해요.', 'A ban locks the account (no console or admin login), revokes all its sessions, and the data plane rejects requests from its keys immediately.'),
        e('同一帳號同一時間只有一條未解除的封禁記錄。', '한 계정에는 해제되지 않은 정지 기록이 동시에 하나만 있어요.', 'An account has at most one open ban record at a time.'),
      ],
      liftTitle: e('解封', '해제', 'Lifting'),
      lift: [
        e('沒有到期自動解封，也沒有梯度時長，所有封禁都要管理員手動解封。', '기간 만료에 따른 자동 해제나 단계별 기간이 없어서, 모든 정지는 관리자가 수동으로 해제해요.', 'There is no automatic expiry or escalating duration; every ban is lifted manually by an admin.'),
        e('解封同樣要填原因，記錄裡保留解封人與原因。', '해제할 때도 사유를 적어야 하고, 해제한 사람과 사유가 기록에 남아요.', 'Lifting also needs a reason; the record keeps who lifted it and why.'),
      ],
      gapTitle: e('沒有對應物', '대응 항목 없음', 'Not available here'),
      gap: [
        e('自動檢測（直連、畫風錨定詞、籤名寬容）與梯度封禁：本倉沒有這類檢測。', '자동 탐지(직접 호출, 화풍 앵커 단어, 서명 관용)와 단계별 정지는 이 시스템에 없어요.', 'Automatic detection (direct calls, style anchors, signature leniency) and escalating bans do not exist here.'),
        e('限流命中與管理員登入失敗沒有記錄，所以這裡看不到它們。', '요청 제한에 걸린 기록과 관리자 로그인 실패는 저장하지 않아서 여기에 보이지 않아요.', 'Rate-limit hits and failed admin logins are not recorded, so they do not show up here.'),
      ],
    },
    colUser: e('使用者', '사용자', 'User'),
    colType: e('類型', '유형', 'Type'),
    colLevel: e('等級', '등급', 'Level'),
    colBanned: e('封禁時間', '정지 시각', 'Banned at'),
    colLifted: e('解封時間', '해제 시각', 'Lifted at'),
    colStatus: e('狀態', '상태', 'Status'),
    colReason: e('原因', '사유', 'Reason'),
    typeAdmin: e('管理員封禁', '관리자 정지', 'Admin ban'),
    awaiting: e('等待手動解封', '수동 해제 대기', 'Awaiting manual lift'),
    active: e('生效中', '적용 중', 'Active'),
    lifted: e('已解除', '해제됨', 'Lifted'),
    internal: e('內部', '내부', 'Internal'),
    empty: e('暫無封禁記錄', '정지 기록이 없어요', 'No ban records'),
    liftReason: e('解封原因：{r}', '해제 사유: {r}', 'Lift reason: {r}'),
  },

  tickets: {
    eyebrow: e('SESSION SECURITY', 'SESSION SECURITY', 'SESSION SECURITY'),
    title: e('會話票據', '세션 티켓', 'Session tickets'),
    desc: e('觀測登入會話的有效率、權杖復用告警與登入安全信號', '로그인 세션의 유효율, 토큰 재사용 경보, 로그인 보안 신호를 관찰해요', 'Login session health, token reuse alerts and sign-in security signals'),
    chip: e('登入會話觀測', '로그인 세션 관찰', 'Session observability'),
    window: e('近 {n} 天', '최근 {n}일', 'Last {n} days'),
    statPolicy: e('會話時長', '세션 유지 시간', 'Session lifetime'),
    statPolicyValue: e('{c} 天 / {a} 小時', '{c}일 / {a}시간', '{c} d / {a} h'),
    statPolicySub: e('控制台 / 管理後台 · 訪問權杖 {ca} / {aa} 分鐘', '콘솔 / 관리자 · 액세스 토큰 {ca} / {aa}분', 'Console / admin · access token {ca} / {aa} min'),
    statTotal: e('觀測會話數', '관찰된 세션 수', 'Sessions observed'),
    statTotalSub: e('近 {n} 天有活動 · 涉及 {a} 個帳號', '최근 {n}일 활동 · 계정 {a}개', 'Active in {n} days · {a} accounts'),
    statValid: e('有效會話佔比', '유효 세션 비율', 'Active session share'),
    statValidSub: e('有效 / 全部會話', '유효 / 전체 세션', 'active / all sessions'),
    statReuse: e('權杖復用告警', '토큰 재사용 경보', 'Token reuse alerts'),
    statReuseSub: e('{a} 個帳號 · 出現即作廢整條會話鏈', '계정 {a}개 · 발생하면 세션 전체가 무효가 돼요', '{a} accounts · the whole chain is revoked'),
    distTitle: e('會話狀態分布', '세션 상태 분포', 'Session state distribution'),
    distCopy: e('快速判斷有效會話佔比和被作廢的會話', '유효 세션 비율과 무효가 된 세션을 빠르게 판단해요', 'Quickly see how many sessions are healthy or revoked'),
    state: {
      active: e('有效會話', '유효 세션', 'Active'),
      revoked: e('已撤銷', '철회됨', 'Revoked'),
      expired: e('已過期', '만료됨', 'Expired'),
      reuse_detected: e('權杖復用', '토큰 재사용', 'Token reuse'),
    } as Record<string, Entry>,
    distLegend: e(
      '有效：未撤銷且未過期。已撤銷：被盜復用、使用者主動退出或帳號被封時作廢。已過期：超過會話時長。每次刷新會產生新權杖，舊權杖只計入「輪換」次數（窗口內 {n} 次）。',
      '유효: 철회·만료되지 않음. 철회: 도용 재사용, 사용자 로그아웃, 계정 정지로 무효화됨. 만료: 세션 유지 시간 초과. 새로 고칠 때마다 새 토큰이 생기고 옛 토큰은 「교체」 횟수로만 세요(기간 내 {n}회).',
      'Active: not revoked and not expired. Revoked: reused token, user sign-out or account ban. Expired: past the session lifetime. Each refresh issues a new token; old ones only count as rotations ({n} in the window).',
    ),
    distEmpty: e('窗口內沒有觀測到會話', '이 기간에 관찰된 세션이 없어요', 'No sessions observed in this window'),
    usersTitle: e('按帳號明細', '계정별 상세', 'By account'),
    usersCopy: e('有權杖復用告警的帳號優先，其次是登入 IP 多的帳號', '토큰 재사용 경보가 있는 계정이 먼저, 그다음 로그인 IP가 많은 계정이 나와요', 'Accounts with reuse alerts first, then those with many sign-in IPs'),
    colUser: e('使用者', '사용자', 'User'),
    colState: e('最近狀態', '최근 상태', 'Latest state'),
    colDist: e('會話分布', '세션 분포', 'Sessions'),
    colIp: e('最近 IP', '최근 IP', 'Last IP'),
    colIps: e('IP 數', 'IP 수', 'IPs'),
    colUa: e('UA', 'UA', 'UA'),
    colActive: e('最近活躍', '최근 활동', 'Last active'),
    distCell: e('有效 {a} · 撤銷 {r} · 過期 {x}', '유효 {a} · 철회 {r} · 만료 {x}', 'active {a} · revoked {r} · expired {x}'),
    reuseCell: e('復用告警 {n}', '재사용 경보 {n}', 'reuse alerts {n}'),
    usersEmpty: e('窗口內沒有帳號活動', '이 기간에 계정 활동이 없어요', 'No account activity in this window'),
    shakesTitle: e('最近登入與會話事件', '최근 로그인과 세션 이벤트', 'Recent sign-ins and session events'),
    shakesCopy: e('保留最近 200 條：登入成功、權杖復用、主動退出', '최근 200건: 로그인 성공, 토큰 재사용, 로그아웃', 'Latest 200: successful sign-ins, token reuse, sign-outs'),
    colTime: e('時間', '시간', 'Time'),
    colResult: e('結果', '결과', 'Result'),
    colReason: e('原因', '사유', 'Reason'),
    colKind: e('端', '종류', 'Kind'),
    result: {
      success: e('登入成功', '로그인 성공', 'Signed in'),
      revoked: e('已撤銷', '철회됨', 'Revoked'),
      reuse_detected: e('權杖復用', '토큰 재사용', 'Token reuse'),
    } as Record<string, Entry>,
    reason: {
      user_signed_out: e('使用者在設備列表裡退出了該會話', '사용자가 기기 목록에서 이 세션을 로그아웃했어요', 'User signed this session out'),
      'session.refresh_reuse': e('刷新權杖被重複使用，整條會話鏈已作廢', '새로 고침 토큰이 재사용되어 세션 전체가 무효가 됐어요', 'Refresh token reused, chain revoked'),
      'oauth.refresh_reuse': e('OAuth 刷新權杖被重複使用', 'OAuth 새로 고침 토큰이 재사용됐어요', 'OAuth refresh token reused'),
      'oauth.code_reuse': e('OAuth 授權碼被重複使用', 'OAuth 인증 코드가 재사용됐어요', 'OAuth authorization code reused'),
    } as Record<string, Entry>,
    kind: {
      console: e('控制台', '콘솔', 'Console'),
      admin: e('管理後台', '관리자', 'Admin'),
    } as Record<string, Entry>,
    shakesEmpty: e('暫無登入與會話事件', '로그인과 세션 이벤트가 없어요', 'No sign-in or session events'),
    noFailedLogin: e('登入失敗不寫審計，所以這裡只有成功登入與告警事件', '로그인 실패는 감사 기록에 남기지 않아서 성공한 로그인과 경보 이벤트만 보여요', 'Failed sign-ins are not audited, so only successful sign-ins and alerts appear'),
  },

  audit: {
    eyebrow: e('CHANGE AUDIT', 'CHANGE AUDIT', 'CHANGE AUDIT'),
    title: e('配置變更記錄', '설정 변경 기록', 'Configuration changes'),
    desc: e(
      '誰在什麼時候改了哪一項定價、路由、渠道與帳號配置。每條記錄與改動在同一個事務裡寫入，改了就一定有記錄',
      '누가 언제 가격, 라우팅, 채널, 계정 설정을 바꿨는지 보여줘요. 기록은 변경과 같은 트랜잭션에 쓰여서 바꾸면 반드시 남아요',
      'Who changed which price, route, channel or account setting and when. Each record is written in the same transaction as the change',
    ),
    chip: e('事務內留痕', '트랜잭션 내 기록', 'Written in-transaction'),
    statTotal: e('近 {n} 天變更', '최근 {n}일 변경', 'Changes in {n} days'),
    statTotalSub: e('當前篩選條件下的記錄數', '현재 필터 조건의 기록 수', 'Records under the current filter'),
    statPricing: e('改價 / 改路由', '가격 / 라우팅 변경', 'Price / route changes'),
    statPricingSub: e('模型 · 渠道 · 渠道模型庫 · 商品', '모델 · 채널 · 채널 모델 라이브러리 · 상품', 'Models · channels · channel library · products'),
    statAutomatic: e('系統與服務操作', '시스템과 서비스 작업', 'System and service actions'),
    statAutomaticSub: e('沒有經過運營帳號手動操作的寫入', '운영 계정의 수동 조작 없이 일어난 쓰기', 'Writes made without a manual operator action'),
    statActors: e('操作帳號數', '작업 계정 수', 'Actors'),
    statActorsSub: e('含系統與服務', '시스템과 서비스 포함', 'Including system and services'),
    sectionTitle: e('變更明細', '변경 상세', 'Change details'),
    sectionCopy: e('合併控制面、帳號服務與支付服務三處的審計記錄', '컨트롤 플레인, 계정 서비스, 결제 서비스 세 곳의 감사 기록을 합쳐요', 'Merges audit rows from control, identity and billing'),
    source: {
      all: e('全部來源', '전체 출처', 'All sources'),
      control: e('控制面', '컨트롤 플레인', 'Control plane'),
      identity: e('帳號', '계정', 'Accounts'),
      billing: e('支付', '결제', 'Billing'),
    } as Record<string, Entry>,
    allTypes: e('全部類型', '전체 유형', 'All types'),
    types: {
      model: e('模型', '모델', 'Model'),
      channel: e('渠道', '채널', 'Channel'),
      channel_model: e('渠道模型庫', '채널 모델 라이브러리', 'Channel model'),
      policy: e('帳號策略', '계정 정책', 'Account policy'),
      key: e('API Key', 'API 키', 'API key'),
      wallet: e('錢包', '지갑', 'Wallet'),
      product: e('商品', '상품', 'Product'),
      order: e('訂單', '주문', 'Order'),
      account: e('帳號', '계정', 'Account'),
      admin: e('管理操作', '관리 작업', 'Admin action'),
      org: e('帳號', '계정', 'Account'),
      session: e('會話', '세션', 'Session'),
      oauth: e('OAuth', 'OAuth', 'OAuth'),
      video: e('影片審核', '영상 심사', 'Video review'),
    } as Record<string, Entry>,
    allActors: e('全部帳號', '전체 계정', 'All actors'),
    allActions: e('全部動作', '전체 동작', 'All actions'),
    action: {
      UPDATE: e('修改', '수정', 'Update'),
      INSERT: e('新建', '생성', 'Create'),
      DELETE: e('刪除', '삭제', 'Delete'),
    } as Record<string, Entry>,
    days: {
      '7': e('近 7 天', '최근 7일', 'Last 7 days'),
      '30': e('近 30 天', '최근 30일', 'Last 30 days'),
      '90': e('近 90 天', '최근 90일', 'Last 90 days'),
      '365': e('近一年', '최근 1년', 'Last year'),
    } as Record<string, Entry>,
    entityPh: e('按對象篩選，如模型 ID、帳號 ID', '대상으로 필터, 예: 모델 ID, 계정 ID', 'Filter by object, e.g. model or account ID'),
    colTime: e('時間', '시간', 'Time'),
    colActor: e('操作者', '작업자', 'Actor'),
    colType: e('類型', '유형', 'Type'),
    colEntity: e('對象', '대상', 'Object'),
    colAction: e('動作', '동작', 'Action'),
    colChanges: e('變更內容', '변경 내용', 'Changes'),
    empty: e('該條件下沒有變更記錄', '이 조건에 해당하는 변경 기록이 없어요', 'No changes under this filter'),
    moreFields: e('另有 {n} 個欄位變化', '필드 {n}개가 더 바뀌었어요', '{n} more fields changed'),
    automaticTag: e('系統自動', '시스템 자동', 'Automatic'),
    totalLine: e('共 {n} 條', '총 {n}건', '{n} total'),
    mergeNote: e(
      '選擇「全部來源」或帳號、支付來源時，每個來源取最近 {n} 條再合併，篩選在這批記錄裡進行；只看控制面時按服務端分頁讀取完整歷史。帳號與支付的審計沒有時間窗口篩選，由頁面按所選天數過濾。',
      '「전체 출처」나 계정, 결제 출처를 고르면 출처마다 최근 {n}건을 가져와 합치고, 그 안에서 필터링해요. 컨트롤 플레인만 보면 서버 페이지네이션으로 전체 기록을 읽어요. 계정과 결제 감사는 기간 필터가 없어서 선택한 일수로 화면에서 걸러요.',
      'With All sources, Accounts or Billing, the latest {n} rows per source are fetched and merged, and filters apply within that batch. Control plane alone is paged on the server with full history. Account and billing audits have no time filter, so the page filters by the chosen days.',
    ),
    mergedNote: e('（僅限各來源最近 {n} 條）', '(출처별 최근 {n}건만)', '(latest {n} per source only)'),
  },

  fallback: {
    eyebrow: e('FALLBACK & ALERTS', 'FALLBACK & ALERTS', 'FALLBACK & ALERTS'),
    chip: e('只讀監控', '읽기 전용 모니터링', 'Read-only monitor'),
    statSwitches: e('降級切換', '폴백 전환', 'Fallback switches'),
    statSwitchesSub: e('窗口內換到備用渠道的次數', '기간 내 예비 채널로 바뀐 횟수', 'Times a request moved to a backup channel'),
    statAllFailed: e('整鏈失敗', '전체 체인 실패', 'All-failed chains'),
    statAllFailedSub: e('所有渠道都失敗的請求', '모든 채널이 실패한 요청', 'Requests where every channel failed'),
    statRate: e('整鏈失敗率', '전체 실패율', 'All-failed rate'),
    statRateSub: e('整鏈失敗 / 窗口內請求', '전체 실패 / 기간 내 요청', 'all-failed / requests in window'),
    statAlerts: e('最近告警', '최근 경보', 'Recent alerts'),
    statAlertsSub: e('嚴重 {c} · 警告 {w}', '심각 {c} · 경고 {w}', 'critical {c} · warning {w}'),
    empty: e('窗口內沒有記錄', '기간 내 기록이 없어요', 'No records in this window'),
    statsTitle: e('按模型統計', '모델별 통계', 'By model'),
    statsCopy: e('降級鏈在上遊抖動時悄悄切換渠道，請求本身看起來成功', '업스트림이 흔들릴 때 폴백 체인이 채널을 조용히 바꿔서 요청은 성공처럼 보여요', 'Fallback chains switch channels quietly, so requests still look successful'),
    eventsTitle: e('降級事件', '폴백 이벤트', 'Fallback events'),
    eventsCopy: e('切換與整鏈失敗兩類事件的流水', '전환과 전체 실패 이벤트 기록', 'Switch and all-failed event stream'),
    alertsTitle: e('運維告警', '운영 경보', 'Operational alerts'),
    alertsCopy: e('渠道自動停用、恢復與整鏈失敗，資料面寫入 Redis 的最近列表', '채널 자동 중지, 복구, 전체 실패. 데이터 플레인이 Redis에 쓴 최근 목록이에요', 'Channel auto-disable, recovery and chain failures from the data plane'),
  },
}
